import React from 'react';
import { motion } from 'framer-motion';
import { Sun, Moon, Home, Footprints, House, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { services, siteConfig } from '../data/mock';
import { trackWhatsAppClick } from '../lib/analytics';

const iconMap = {
  Sun: Sun,
  Moon: Moon,
  Home: Home,
  Footprints: Footprints,
  House: House,
};

// Unified soft color palette - professional and calm
const cardStyle = {
  backgroundColor: '#FAFAF8',
  borderColor: 'rgba(91, 154, 111, 0.15)'
};

const ServicesSection = () => {
  const generateWhatsAppLink = (serviceName) => {
    const message = encodeURIComponent(
      `Hola Fabio, me gustaría información sobre ${serviceName}. ¿Tienes disponibilidad?`
    );
    return `${siteConfig.whatsappLink}?text=${message}`;
  };

  return (
    <section id="servicios" className="py-20 bg-page">
      <div className="container mx-auto px-4 md:px-8">
        {/* Section header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="heading-1 mb-4">Mis servicios</h2>
          <p className="body-medium text-secondary max-w-2xl mx-auto">
            Ofrezco diferentes opciones de cuidado para adaptarme a las necesidades de cada perro y familia.
          </p>
        </motion.div>

        {/* Services grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service, index) => {
            const IconComponent = iconMap[service.icon];

            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card 
                  className="h-full flex flex-col overflow-hidden rounded-2xl shadow-sm hover:shadow-md transition-shadow"
                  style={{ 
                    backgroundColor: cardStyle.backgroundColor,
                    border: `1px solid ${cardStyle.borderColor}`
                  }}
                >
                  {/* Service Image (if exists) */}
                  {service.image && (
                    <div className="w-full flex items-center justify-center p-2" style={{ backgroundColor: 'rgba(255,255,255,0.5)' }}>
                      <img 
                        src={service.image} 
                        alt={service.title}
                        className="w-full h-auto object-contain max-h-52 rounded-lg"
                      />
                    </div>
                  )}
                  <CardHeader className={service.image ? "pb-4 pt-4" : "pb-4 pt-6"}>
                    {!service.image && (
                      <div className="w-12 h-12 rounded-full flex items-center justify-center mb-4" style={{ backgroundColor: 'rgba(91, 154, 111, 0.12)' }}>
                        {IconComponent && <IconComponent className="w-6 h-6" style={{ color: '#5B9A6F' }} />}
                      </div>
                    )}
                    <CardTitle className="text-lg font-semibold text-gray-800">{service.title}</CardTitle>
                    <CardDescription className="text-sm text-gray-600 leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardFooter className="mt-auto pt-4 pb-6">
                    <a
                      href={generateWhatsAppLink(service.title)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full"
                      onClick={() => trackWhatsAppClick('services', service.title)}
                      data-testid={`whatsapp-btn-service-${service.id}`}
                    >
                      <Button className="btn-primary rounded-full w-full gap-2">
                        <MessageCircle className="w-4 h-4" />
                        Pedir disponibilidad
                      </Button>
                    </a>
                  </CardFooter>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
